/**
 * UsageLimitAlertService - Manages chat usage limit alerts
 * Handles checking usage thresholds and generating appropriate alerts
 */
class UsageLimitAlertService {

    // Alert thresholds
    static THRESHOLDS = {
        MODERATE: 50,
        HIGH: 75,
        CRITICAL: 90,
        LIMIT_REACHED: 100
    };

    /**
     * Check usage and generate alerts
     * @param {number} currentUsage Current monthly chat usage
     * @param {number} limit Monthly chat limit
     * @param {string} planType Current plan type
     * @returns {Array} Array of alert objects
     */
    static checkUsageAlerts(currentUsage, limit, planType) {
        const alerts = [];
        const percentage = (currentUsage / limit) * 100;

        // Generate alerts based on thresholds
        if (percentage >= this.THRESHOLDS.LIMIT_REACHED) {
            alerts.push(this.createAlert(
                'limit-reached',
                'error',
                `🚨 You've reached your monthly chat limit`,
                `You've used ${currentUsage.toLocaleString()}/${limit.toLocaleString()} chats this month. Consider upgrading your plan to continue.`,
                planType,
                percentage,
                true // persistent
            ));
        } else if (percentage >= this.THRESHOLDS.CRITICAL) {
            alerts.push(this.createAlert(
                'critical-usage',
                'error',
                `⚠️ You're close to your monthly chat limit`,
                `You've used ${currentUsage.toLocaleString()}/${limit.toLocaleString()} chats this month (${Math.round(percentage)}%). Consider upgrading soon.`,
                planType,
                percentage,
                true
            ));
        } else if (percentage >= this.THRESHOLDS.HIGH) {
            alerts.push(this.createAlert(
                'high-usage',
                'warning',
                `📊 You're approaching your monthly chat limit`,
                `You've used ${currentUsage.toLocaleString()}/${limit.toLocaleString()} chats this month (${Math.round(percentage)}%). You might want to consider upgrading your plan.`,
                planType,
                percentage,
                true
            ));
        } else if (percentage >= this.THRESHOLDS.MODERATE) {
            alerts.push(this.createAlert(
                'moderate-usage',
                'info',
                `📈 You've used half your monthly chat limit`,
                `You've used ${currentUsage.toLocaleString()}/${limit.toLocaleString()} chats this month (${Math.round(percentage)}%). You're on track for a productive month!`,
                planType,
                percentage,
                true
            ));
        }

        return alerts;
    }

    /**
     * Create a standardized alert object
     */
    static createAlert(id, severity, title, message, planType, percentage, persistent = false) {
        return {
            id,
            type: 'usage-limit',
            severity, // 'info', 'warning', 'error'
            title,
            message,
            persistent,
            timestamp: new Date().toISOString(),
            metadata: {
                planType,
                percentage: Math.round(percentage * 100) / 100,
                threshold: this.getThresholdName(percentage)
            },
            actions: percentage >= this.THRESHOLDS.HIGH ? [{
                label: 'Upgrade Plan',
                action: 'navigate',
                target: '/account-settings',
                state: { activeTab: 1 }
            }] : []
        };
    }

    /**
     * Get threshold name for percentage
     */
    static getThresholdName(percentage) {
        if (percentage >= this.THRESHOLDS.LIMIT_REACHED) return 'LIMIT_REACHED';
        if (percentage >= this.THRESHOLDS.CRITICAL) return 'CRITICAL';
        if (percentage >= this.THRESHOLDS.HIGH) return 'HIGH';
        if (percentage >= this.THRESHOLDS.MODERATE) return 'MODERATE';
        return 'LOW';
    }

    /**
     * Check if alert should be shown (considering dismissal state)
     * @param {string} alertId Alert ID
     * @param {string} tenantId Tenant ID (for storage key)
     * @returns {boolean} Whether alert should be shown
     */
    static shouldShowAlert(alertId, tenantId = 'default') {
        // Usage limit alerts should always be shown when the threshold is met
        // They are only dismissed when the underlying issue is resolved
        // (i.e., when usage drops below threshold or user upgrades plan)

        // For usage alerts, we don't respect dismissal - they're always shown
        // when the usage condition is met. This ensures users are continuously
        // aware of their usage status until they take action.
        return true;

        // Note: The old logic below would hide alerts for a day when dismissed,
        // but for usage alerts we want them to persist until the issue is resolved

        // const dismissedAlerts = this.getDismissedAlerts(tenantId);
        // const dismissedInfo = dismissedAlerts[alertId];
        // 
        // // If never dismissed, show alert
        // if (!dismissedInfo) return true;
        // 
        // // If dismissed today, don't show
        // const today = new Date().toDateString();
        // const dismissedDate = new Date(dismissedInfo.dismissedAt).toDateString();
        // 
        // return today !== dismissedDate;
    }

    /**
     * Dismiss (snooze) an alert temporarily
     * For usage alerts, this is more of a "snooze" since they should persist
     * until the underlying issue is resolved
     */
    static dismissAlert(alertId, tenantId = 'default') {
        // For now, we still record the dismissal for potential future use
        // But the shouldShowAlert method ignores dismissals for usage alerts
        const dismissedAlerts = this.getDismissedAlerts(tenantId);
        dismissedAlerts[alertId] = {
            dismissedAt: new Date().toISOString(),
            count: (dismissedAlerts[alertId]?.count || 0) + 1
        };

        localStorage.setItem(`dismissedAlerts_${tenantId}`, JSON.stringify(dismissedAlerts));

        // Note: Even though we record the dismissal, usage alerts will still
        // be shown the next time the page loads or alerts are refreshed
        // because shouldShowAlert always returns true for usage alerts
    }

    /**
     * Get dismissed alerts from localStorage
     */
    static getDismissedAlerts(tenantId = 'default') {
        try {
            const stored = localStorage.getItem(`dismissedAlerts_${tenantId}`);
            return stored ? JSON.parse(stored) : {};
        } catch (error) {
            console.error('Error reading dismissed alerts:', error);
            return {};
        }
    }

    /**
     * Clear old dismissals (older than 30 days)
     */
    static cleanupDismissedAlerts(tenantId = 'default') {
        const dismissedAlerts = this.getDismissedAlerts(tenantId);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const cleaned = {};
        Object.entries(dismissedAlerts).forEach(([alertId, info]) => {
            const dismissedDate = new Date(info.dismissedAt);
            if (dismissedDate > thirtyDaysAgo) {
                cleaned[alertId] = info;
            }
        });

        localStorage.setItem(`dismissedAlerts_${tenantId}`, JSON.stringify(cleaned));
    }
}

export default UsageLimitAlertService;
